import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Insight } from '../../../../common/hooks/use-insights-api.hook';
import {
    actionLabelKey,
    actionRoute,
    findingText,
    contentBrief,
    relativeTime,
    severityStyle,
    startsAnalysis,
    startsCategorisation,
} from '../insight-presentation';
import { signalRunStarted } from '../../work/agent-work.store';
import { useInlineAnswer } from '../use-inline-answer.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { putBrief } from '../../../../common/services/content-brief.service';

interface Props {
    finding: Insight;
    onDismiss: (findingId: string) => void;
    /**
     * The finding was answered on the card (a profile field was saved). The
     * server has already recomputed the setup gaps; the panel should drop
     * this one and reload, because answering one gap can open the next.
     */
    onResolved?: (findingId: string) => void;
    busy?: boolean;
}

/**
 * One finding.
 *
 * Three tiers of information, in the order a vendor needs them: what happened
 * (title), the evidence (detail), and what to do (the panel and its button).
 * The evidence is quoted rather than paraphrased — the whole point of the
 * deterministic engine is that every claim traces back to a metric.
 */
export const InsightCard: React.FC<Props> = ({ finding, onDismiss, onResolved, busy }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const studio = useContentStudioApi();
    const style = severityStyle(finding.severity, finding.sentiment);
    const when = relativeTime(finding.lastSeenAt);
    const route = actionRoute(finding);
    const { field, draft, setDraft, save: saveInline, saving, error: saveError } = useInlineAnswer(
        { id: finding.findingId, actionKind: finding.suggestedAction?.kind },
        onResolved,
    );

    const [starting, setStarting] = useState(false);

    const act = async () => {
        if (!route) return onDismiss(finding.findingId);

        // An external destination (the vendor's own page) opens in a new tab.
        if (/^https?:\/\//.test(route)) {
            window.open(route, '_blank', 'noopener');
            return undefined;
        }

        /**
         * The analyst is started here, not on Work: the card is the promise
         * "we can analyse this", and the click is the consent.
         */
        if (startsAnalysis(finding)) {
            setStarting(true);
            try {
                await studio.createAgentRun({
                    profile: 'business-analyst',
                    message: t('INSIGHTS.BRIEF.RUN_ANALYSIS') as string,
                });
                signalRunStarted();
            } catch {
                setStarting(false);
                return undefined;
            }
            setStarting(false);
        }

        /*
         * Scoped to the products that have no category, which is the whole
         * point of the card: the tree is already built and this is filing what
         * arrived after it. Handing the agent the entire catalogue instead
         * would cost several times as much and invite it to rearrange work the
         * vendor has already accepted.
         */
        if (startsCategorisation(finding)) {
            setStarting(true);
            try {
                await studio.createAgentRun({
                    profile: 'categoriser',
                    // Creating categories and assigning products are writes;
                    // pressing "Fix it" on a card that says exactly what will
                    // happen is the approval, so the run does not stop to ask
                    // for it a second time.
                    autonomy: 'approved',
                    message: t('INSIGHTS.BRIEF.CATEGORISE_PRODUCTS') as string,
                    context: { onlyUncategorised: true },
                });
                signalRunStarted();
            } catch {
                setStarting(false);
                return undefined;
            }
            setStarting(false);
        }

        /**
         * A finding that asks for work sends its brief along, so the studio
         * opens knowing what it was asked for.
         */
        if (finding.suggestedAction?.kind === 'generate_content') {
            const params = (finding.suggestedAction.params ?? {}) as { productUuid?: string };
            putBrief(contentBrief(finding, t as never), params.productUuid);
        }

        return navigate(route);
    };

    return (
        <Card className={cn('h-full gap-3 py-4 transition-opacity', busy && 'opacity-55')}>
            <CardContent className="flex h-full flex-col gap-3 px-4">
                {/* ── severity + when ─────────────────────────────── */}
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn('gap-1.5 px-2 py-0.5 text-[11px]', style.badge)}>
                        <span className={cn('size-1.5 rounded-full', style.dot)} />
                        {t(style.labelKey)}
                    </Badge>

                    <span className="ms-auto flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarDays className="size-3" />
                        {t(when.key, { count: when.count })}
                    </span>

                    {/*
                      Dismiss is an icon, not a labelled button: parking a
                      finding is a small reversible act and must not compete
                      with the action the card is recommending.
                    */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-6 text-muted-foreground"
                        disabled={busy}
                        onClick={() => onDismiss(finding.findingId)}
                        aria-label={t('INSIGHTS.DISMISS') as string}
                    >
                        <X className="size-3.5" />
                    </Button>
                </div>

                {/* ── what happened ───────────────────────────────── */}
                <p className="text-sm font-medium leading-snug">
                    {findingText(t, finding.title, { key: finding.i18n?.title, params: finding.i18n?.params })}
                </p>

                {/* ── the evidence, quoted ────────────────────────── */}
                <div className="border-s-2 ps-3">
                    <p className="text-[13px] leading-relaxed text-muted-foreground">
                        {findingText(t, finding.detail, { key: finding.i18n?.detail, params: finding.i18n?.params })}
                    </p>
                    {finding.consequence && (
                        <p className="mt-1.5 text-[13px] font-medium leading-relaxed">
                            {findingText(t, finding.consequence, {
                                key: finding.i18n?.consequence,
                                params: finding.i18n?.params,
                            })}
                        </p>
                    )}
                </div>

                {/*
                  There was a "WHAT TO DO NEXT" panel here. What filled it were
                  the `ACTION.*_BODY` strings, which were written as hints about
                  the button — "Two or more competitor websites, separated by
                  commas", sitting above the very input that says the same in
                  its placeholder. A heading promising an instruction over text
                  that restated the button cost a third of the card's height to
                  say one thing three times. The button says it.
                */}

                {/*
                  A gap that is one field is answered here, so the card still
                  ends in exactly one thing to do.
                */}
                {field ? (
                    <form
                        className="mt-auto flex flex-col gap-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void saveInline();
                        }}
                    >
                        <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            disabled={busy || saving}
                            aria-invalid={saveError || undefined}
                            aria-label={t(actionLabelKey(finding)) as string}
                            placeholder={
                                t(
                                    field === 'websiteUrl'
                                        ? 'INSIGHTS.INLINE.WEBSITE_PLACEHOLDER'
                                        : 'INSIGHTS.INLINE.COMPETITORS_PLACEHOLDER',
                                ) as string
                            }
                        />
                        {saveError && <p className="text-xs text-destructive">{t('INSIGHTS.INLINE.SAVE_FAILED')}</p>}
                        <Button type="submit" variant="outline" size="sm" disabled={busy || saving || !draft.trim()}>
                            {saving ? t('INSIGHTS.INLINE.SAVING') : t(actionLabelKey(finding))}
                            <ChevronRight className="size-4" />
                        </Button>
                    </form>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-auto w-full"
                        disabled={busy || starting}
                        onClick={() => void act()}
                    >
                        {starting
                            ? t('INSIGHTS.INLINE.STARTING')
                            : t(route ? actionLabelKey(finding) : 'INSIGHTS.ACKNOWLEDGE')}
                        <ChevronRight className="size-4" />
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};
